import React, { useState } from 'react';
import { Plus, FolderOpen } from 'lucide-react';
import PillDropdown from '../../components/ui/PillDropdown';
import { DARK, btnStyle } from './theme';

// The Ingest tab's primary view — every card insertion creates a row here
// the moment it's detected (card-ingest.js creates the 'queued' record
// before any scan/offload happens), so this is a live list of ingestions,
// not just a history of finished ones. This form is the manual entry point:
// pick any folder or hard disk (not just a detected card) as the source.
export default function NewIngestForm({ c, onCreate }) {
  const isDarkMode = c === DARK;
  const [open, setOpen] = useState(false);
  const [sourcePath, setSourcePath] = useState('');
  const [destination, setDestination] = useState('');
  const [operation, setOperation] = useState('copy');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const pickSource = async () => {
    const result = await window.electronAPI.cardIngest.chooseSourceFolder(sourcePath);
    if (result?.success) setSourcePath(result.path);
  };
  const pickDestination = async () => {
    const result = await window.electronAPI.cardIngest.chooseDestination();
    if (result?.success) setDestination(result.path);
  };
  const submit = async () => {
    if (busy || !sourcePath || !destination) return;
    setBusy(true);
    setError(null);
    const result = await onCreate({ sourcePath, destination, operation });
    setBusy(false);
    if (!result?.success) setError(result?.error || 'Could not create ingest');
    else setOpen(false);
  };

  return (
    <div style={{ border: `1px solid ${c.rule}`, borderRadius: '8px', padding: '12px' }}>
      <button disabled={busy} onClick={() => setOpen(v => !v)} style={{ ...btnStyle(c, 'primary'), opacity: busy ? 0.55 : 1 }}>
        <Plus size={13} /> New ingest
      </button>
      {open && (
        <fieldset disabled={busy} aria-busy={busy} style={{ margin: '12px 0 0', padding: 0, border: 0, display: 'grid', gap: '9px', opacity: busy ? 0.55 : 1 }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={pickSource} style={btnStyle(c, 'default')}><FolderOpen size={13} /> Source folder / hard disk</button>
            <span style={{ color: c.muted, fontSize: '11px', alignSelf: 'center', wordBreak: 'break-all' }}>{sourcePath || 'Choose the footage folder'}</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={pickDestination} style={btnStyle(c, 'default')}><FolderOpen size={13} /> Destination</button>
            <span style={{ color: c.muted, fontSize: '11px', alignSelf: 'center', wordBreak: 'break-all' }}>{destination || 'Choose where output goes'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            Operation
            <PillDropdown
              value={operation}
              onChange={setOperation}
              isDarkMode={isDarkMode}
              options={[
                { value: 'copy', label: 'Verified copy' },
                { value: 'copy_and_proxy', label: 'Verified copy + proxy' },
                { value: 'proxy_only', label: 'Proxy only (no copy)' },
              ]}
            />
          </div>
          <button disabled={busy || !sourcePath || !destination} onClick={submit} style={{ ...btnStyle(c, 'primary'), width: 'fit-content' }}>
            {busy ? 'Creating…' : 'Create ingest'}
          </button>
          {error && <div style={{ color: c.err, fontSize: '11px' }}>{error}</div>}
        </fieldset>
      )}
    </div>
  );
}
